import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import styles from './BlogPage.module.css';

export const BlogPost = ({ postPath }) => {
    const [postContent, setPostContent] = useState('');
    const [frontMatter, setFrontMatter] = useState({});

    useEffect(() => {
        fetch(postPath)
        .then(response => response.text())
        .then(text => {
            const frontMatterText = extractFrontMatter(text);
            if (frontMatterText) {
                setFrontMatter(parseFrontMatter(frontMatterText));
                // Remove front matter and any leading empty lines
                const contentWithoutFrontMatter = text.replace(/^---[\s\S]*?---\s*/, '');
                setPostContent(contentWithoutFrontMatter);
            } else {
                setPostContent(text);
            }
        })
        .catch(error => console.error('Error fetching the markdown file: ', error));
    }, [postPath]);

    useEffect(() => {
        import('./github-dark.css');
        import('highlight.js').then(hljs => {
            // Add IDs to headings for navigation
            document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
                if (!heading.id) {
                    const id = heading.textContent.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                    heading.id = id;
                }
            });
            
            // Highlight code blocks
            document.querySelectorAll('pre code').forEach((block) => {
                hljs.default.highlightBlock(block);
            });
        });
    }, [postContent]);

    const processedContent = useMemo(() => {
        let content = postContent;
        
        // Remove the first h1 if it matches the title from front matter
        if (frontMatter.title && content) {
            const firstH1Regex = new RegExp(`^#\\s+${frontMatter.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\n`, 'm');
            content = content.replace(firstH1Regex, '');
        }

        // Handle math delimiters: \[...\] -> $$...$$ and \(...\) -> $...$
        content = content
            .replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$')
            .replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$');

        return content;
    }, [postContent, frontMatter.title]);

    return (
        <>
            {/* Table of Contents Sidebar */}
            <MarkdownSidebar markdown={postContent} />

            {/* Main Article Content */}
            <div className={styles.articleContent}>
                {/* Article Header */}
                <header className={styles.articleHeader}>
                    <h1 className={styles.articleTitle}>{frontMatter.title}</h1>
                    {frontMatter.summary && (
                        <p className={styles.articleSummary}>{frontMatter.summary}</p>
                    )}
                    <div className={styles.articleMeta}>
                        {frontMatter.author && (
                            <span className={styles.articleAuthor}>
                                By {frontMatter.author}
                            </span>
                        )}
                        {frontMatter.date && (
                            <span className={styles.articleDate}>
                                {frontMatter.date}
                            </span>
                        )}
                        {frontMatter.tags && Array.isArray(frontMatter.tags) && (
                            <div className={styles.articleTags}>
                                {frontMatter.tags.map((tag, index) => (
                                    <span key={index} className={styles.tag}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </header>

                {/* Article Content */}
                <article className={styles.markdownContent}>
                    <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                    >
                        {processedContent}
                    </ReactMarkdown>
                </article>
            </div>
        </>
    );
};


export const extractFrontMatter = markdown => {
    const match = /^---\s*\n([\s\S]*?)\n---/.exec(markdown);
    return match ? match[1] : null;
};

export const parseFrontMatter = frontMatter => {
    const lines = frontMatter.split('\n');
    return lines.reduce((acc, line) => {
        const trimmedLine = line.trim();
        
        // Skip empty lines and comments
        if (!trimmedLine || trimmedLine.startsWith('#')) {
            return acc;
        }
        
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex > 0) {
            const key = trimmedLine.substring(0, colonIndex).trim();
            let value = trimmedLine.substring(colonIndex + 1).trim();
            
            // Handle array values like [tag1, tag2, tag3]
            if (value.startsWith('[') && value.endsWith(']')) {
                value = value.slice(1, -1).split(',').map(item => item.trim());
            }
            
            // Remove inline comments
            if (typeof value === 'string' && value.includes(' #')) {
                value = value.split(' #')[0].trim();
            }
            
            if (key && value) {
                acc[key] = value;
            }
        }
        return acc;
    }, {});
};

export const MarkdownRenderer = ({ markdownText }) => {
    // Handle math delimiters: \[...\] -> $$...$$ and \(...\) -> $...$
    const processedText = markdownText
        .replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$')
        .replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$');
    
    return (
        <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeRaw]}
        >
            {processedText}
        </ReactMarkdown>
    );
}
  


export const MarkdownSidebar = ({ markdown }) => {
    const [headers, setHeaders] = useState([]);
    // const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        const lines = markdown.replace(/```[\s\S]+?\n```/g, '').replace(/<!--[\s\S]+?-->/g, '').split('\n');
        const headerRegex = /^(#+)\s+(.*)/;
        const extractedHeaders = lines
        .map(line => {
            const match = line.match(headerRegex);
            if (match) {
                const depth = match[1].length;
                const text = match[2].replace(/\*/g,'').trim();
                const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                return { depth, text, id };
            }
            return null;
        })
        .filter(header => header !== null);

        setHeaders(extractedHeaders);
    }, [markdown]);

    useEffect(() => {
        // Set up intersection observer for active section tracking
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            {
                rootMargin: '-100px 0px -60% 0px', // Trigger when header is near the top
                threshold: 0
            }
        );

        // Observe all headings
        const headingElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headingElements.forEach(heading => {
            if (heading.id) {
                observer.observe(heading);
            }
        });

        return () => {
            headingElements.forEach(heading => {
                if (heading.id) {
                    observer.unobserve(heading);
                }
            });
        };
    }, [markdown]);

    const handleNavigation = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });
            setActiveSection(id);
        }
    };

    // const toggleCollapsed = () => {
    //     setIsCollapsed(!isCollapsed);
    // };

    return (
        // <aside className={`${styles.tocSidebar} ${isCollapsed ? styles.collapsed : ''}`}>
        <aside className={`${styles.tocSidebar}`}>
            <div className={styles.tocHeader}>
                <h3 className={styles.tocTitle}>Table of Contents</h3>
                {/* <button 
                    className={styles.tocToggle}
                    onClick={toggleCollapsed}
                    aria-label={isCollapsed ? "Expand table of contents" : "Collapse table of contents"}
                >
                    {isCollapsed ? '▶' : '◀'}
                </button> */}
            </div>
            
            <div className={styles.tocContent}>
                <nav className={styles.tocNav}>
                    {headers.map(header => (
                        <button
                            key={header.id}
                            className={`${styles.tocItem} ${styles[`tocLevel${header.depth}`]} ${
                                activeSection === header.id ? styles.active : ''
                            }`}
                            onClick={() => handleNavigation(header.id)}
                            title={header.text}
                        >
                            {header.text}
                        </button>
                    ))}
                </nav>
            </div>
        </aside>
    );
};
